// App.js
import React, { useEffect, useState } from "react";
import { getToken, getUserData } from "./services/oauthService";
import "./App.css";
import {
  HashRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";

import Home from "./Home";
import Nomes from "./Nomes";  
import Distribuicoes from "./Distribuicoes";  
import Status from "./Status";
import { AppLayout } from "./components/layout/AppLayout";

const App = () => {
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    const handleAuthentication = async () => {
      try {
        setIsLoading(true);
        const query = new URLSearchParams(window.location.search);
        const code = query.get("code");
        const localDevToken = import.meta.env.REACT_APP_LOCAL_DEV_TOKEN;
        const env = import.meta.env.REACT_APP_ENV;
        const storedToken = localStorage.getItem("access_token");
        
        // Limpar a URL após obter o código para evitar problemas de refresh
        // Preserva o hash para compatibilidade com HashRouter no Firefox
        if (code && window.history.replaceState) {
          const hash = window.location.hash || '#/';
          const newUrl = window.location.origin + window.location.pathname + hash;
          window.history.replaceState({ path: newUrl }, '', newUrl);
        }

        if (localDevToken && env === "development") {
          // Se o token local de desenvolvimento estiver presente, considera o usuário autenticado
          console.log("Token de desenvolvimento detectado. Usuário autenticado.");
          localStorage.setItem("access_token", localDevToken);
          try {
            const data = await getUserData(localDevToken);
            setUserData(data);
          } catch (error) {
            console.error("Erro ao obter dados do usuário com token de desenvolvimento:", error);
            setAuthError(error.isNotAdmin ? error.message : "Erro ao obter dados do usuário");
            localStorage.removeItem("access_token");
          }
        } else if (code) {
          try {
            const token = await getToken(code);
            localStorage.setItem("access_token", token);
            
            try {
              const data = await getUserData(token);
              setUserData(data);
            } catch (userDataError) {
              console.error("Erro ao obter dados do usuário:", userDataError);
              setAuthError(userDataError.isNotAdmin ? userDataError.message : "Erro ao obter dados do usuário");
              localStorage.removeItem("access_token");
            }
          } catch (tokenError) {
            console.error("Erro ao obter token:", tokenError);
            setAuthError("Erro na autenticação");
            localStorage.removeItem("access_token");
            redirectToOAuth();
          }
        } else if (storedToken) {
          try {
            const data = await getUserData(storedToken);
            setUserData(data);
          } catch (error) {
            console.error("Token armazenado inválido:", error);
            if (error.isNotAdmin) {
              setAuthError(error.message);
            } else {
              localStorage.removeItem("access_token");
              redirectToOAuth();
            }
          }
        } else {
          console.log("Redirecionando para autenticação...");
          // Caso contrário, redireciona para o OAuth
          redirectToOAuth();
        }
      } catch (error) {
        console.error("Erro geral na autenticação:", error);
        setAuthError("Erro na autenticação");
        localStorage.removeItem("access_token");
      } finally {
        setIsLoading(false);
      }
    };

    handleAuthentication();
  }, []);

  const redirectToOAuth = () => {
    // Com HashRouter, precisamos garantir que a URL de redirecionamento não inclua o hash
    const redirectUri = window.location.origin;
    const authorizeUrl = `${import.meta.env.REACT_APP_OAUTH_AUTHORIZE_URL}?response_type=code&client_id=${import.meta.env.REACT_APP_CLIENT_ID}&redirect_uri=${redirectUri}&scope=openid`;
    window.location.href = authorizeUrl;
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando, por favor aguarde...</p>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="error-container">
        <h2>⚠️ Acesso Restrito</h2>
        <p>{authError}</p>
        <p style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
          Entre em contato com o administrador do sistema para obter acesso.
        </p>
        <button onClick={redirectToOAuth}>Fazer login com outra conta</button>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={<Navigate to="/home" />} />
        <Route
          element={
            <ProtectedRoute userData={userData}>
              <AppLayout userData={userData} />
            </ProtectedRoute>
          }
        >
          <Route path="/home" element={<Home />} />
          <Route path="/status" element={<Status />} />
          <Route path="/nomes" element={<Nomes />} />
          <Route path="/distribuicoes" element={<Distribuicoes />} />
        </Route>
      </Routes>
    </Router>
  );
};

const ProtectedRoute = ({ children, userData }) => {
  const token =
    import.meta.env.REACT_APP_LOCAL_DEV_TOKEN ||
    localStorage.getItem("access_token");
  return token && userData ? children : <Navigate to="/" />;
};

export default App;
